"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CardProvenanceLookupProjection = exports.BoundLegLedger = exports.CardProvenanceLeg = exports.SyncCardProvenanceUploadResponse = exports.SyncCardProvenanceRecordAck = exports.CardProvenanceConflictCode = exports.CardProvenanceAckStatus = exports.SyncTransactionRecordCardProvenance = exports.SyncTransactionPaymentCardProvenance = exports.SyncTransactionLineCardProvenance = exports.CardProvenanceWitness = exports.CARD_PROVENANCE_LEDGER_MAX_COUNTED = exports.SYNC_CARD_PROVENANCE_CAPABILITY = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
const sync_js_1 = require("./sync.js");
/**
 * RL-847: cross-terminal provenance for card-bound refund lines (design r2,
 * `docs/engine/design/RL-847-refund-provenance.md` in the terminals repo).
 *
 * Some goods may be refunded only to the card leg that paid them — today the
 * Varus National Cashback lines. The selling till knows which lines a bound leg
 * paid; these schemas carry that fact to Back Office, and back to a till that
 * did not sell the goods, without any card data.
 *
 * **Schema isolation (§3.3).** Every older transaction route shares
 * `SyncTransactionRecord`, and that schema does NOT gain these fields: a
 * stripped witness or line marker there leaves no provenance, and a leg flag
 * there is refused by the strict leg. Only `POST /sync/transactions/card-provenance`
 * parses `SyncTransactionRecordCardProvenance`.
 *
 * **Capability.** `sync.card-provenance-v1` is the handshake key a till waits
 * for before it freezes provenance into a sale. Step 1 ships the route with the
 * key NOT advertised; once advertised it is never withdrawn.
 */
exports.SYNC_CARD_PROVENANCE_CAPABILITY = 'sync.card-provenance-v1';
/** The most refund receipts a served bound-leg ledger names (§3.4). */
exports.CARD_PROVENANCE_LEDGER_MAX_COUNTED = 64;
/**
 * A lower-case identifier this contract defines (a producer, a payment binding
 * id): `[a-z0-9-]{1,40}` with at most twelve digits, the same digit defence as
 * `CardTransactionReference`, so a card number mis-mapped into the slot is
 * refused rather than stored and served (review of step 1, 7-39771 P2).
 */
const ProvenanceIdentifier = zod_1.z
    .string()
    .regex(/^[a-z0-9-]{1,40}$/)
    .refine((value) => value.replace(/\D/g, '').length <= 12, {
    message: 'identifier must not carry more than twelve digits',
});
/**
 * Money in canonical base-10 form: no leading zero, no `-0`. The route admits
 * only this form for what the provenance rules compare and the projection
 * serves, so an accepted record can never produce a projection the served
 * schema refuses (7-39771 P2).
 */
const CanonicalMinor = pricing_js_1.MoneyMinor.refine((value) => /^(0|-?[1-9]\d*)$/.test(value), {
    message: 'minor units must be a canonical base-10 integer',
});
const CanonicalPositiveMinor = pricing_js_1.MoneyMinor.refine((value) => /^[1-9]\d*$/.test(value), {
    message: 'payment amountMinor must be a positive canonical base-10 integer',
});
/**
 * The producer's positive statement that it examined every leg and every
 * exported line of THIS record, and that the markers it carries are all of
 * them. Without it nothing in the record is provenance: a missing marker is
 * never read as "not bound". The counts make any partial strip detectable.
 */
exports.CardProvenanceWitness = zod_1.z
    .object({
    version: zod_1.z.literal(1),
    /** Which producer emitted the markers, e.g. `varus-ncb`. Back Office knows a fixed set. */
    producer: ProvenanceIdentifier,
    /** = `payments.length`. */
    legCount: zod_1.z.number().int().min(0).max(32),
    /** Legs with method `card`. */
    cardLegCount: zod_1.z.number().int().min(0).max(32),
    /** Legs with `cardBound: true`. */
    boundLegCount: zod_1.z.number().int().min(0).max(32),
    /** Lines carrying `refundCardRrn`. */
    boundLineCount: zod_1.z.number().int().min(0).max(500),
})
    .strict();
/** A sale line on the card-provenance route. */
exports.SyncTransactionLineCardProvenance = sync_js_1.SyncTransactionLine.extend({
    lineTotalMinor: CanonicalMinor,
    /** The RRN of the card leg that alone may take this line's refund. */
    refundCardRrn: sync_js_1.CardTransactionReference.optional(),
});
/** A tender leg on the card-provenance route; as strict as the 1.0 leg. */
exports.SyncTransactionPaymentCardProvenance = sync_js_1.SyncTransactionPayment.extend({
    amountMinor: CanonicalPositiveMinor,
    /**
     * `true`: this card leg paid exactly the lines whose `refundCardRrn` equals
     * its `rrn`. `false`: an ordinary card leg. Present on every card leg of a
     * witnessed record; absent on non-card legs and on unwitnessed records.
     */
    cardBound: zod_1.z.boolean().optional(),
    /** The acquirer's merchant id from the slip (O5). */
    paymentMerchantId: sync_js_1.CardTransactionReference.optional(),
    /** The factory binding id the leg was taken through (O5, RL-848 §3.1). */
    paymentBindingId: ProvenanceIdentifier.optional(),
}).strict();
/**
 * The record the card-provenance route parses: the 1.0 record (with the
 * `identifiers` and `payment-legs` fields it already carries) plus the
 * provenance fields.
 *
 * **A combined route (owner decision, 2026-10-26).** The route also carries
 * `extensions`, the target-owned frozen facts every Varus sale upload has
 * (`rl.varus`, the document-feed facts, the order and the metrics), so a
 * provenance sale needs no second route. It is the SAME schema object as on
 * `POST /sync/transactions/extensions` — the record's, each line's and each
 * leg's — never a copy: validated, stored and replayed exactly as there. Back
 * Office keeps the first upload's `extensions` and does not compare them on a
 * replay; the provenance rules never read them, so they neither quarantine a
 * sale nor reach the lookup projection.
 */
exports.SyncTransactionRecordCardProvenance = sync_js_1.SyncTransactionRecord.extend({
    extensions: sync_js_1.SyncTransactionRecord.shape.extensions,
    lines: zod_1.z.array(exports.SyncTransactionLineCardProvenance).min(1),
    payments: zod_1.z.array(exports.SyncTransactionPaymentCardProvenance).max(32).optional(),
    cardProvenanceWitness: exports.CardProvenanceWitness.optional(),
});
/**
 * What Back Office holds for the record after this upload:
 * - `stored` — complete provenance (V0–V7 hold, nothing quarantined);
 * - `absent` — no witness, so nothing is provenance;
 * - `quarantined` — witnessed but failing a rule, or contradicted on replay.
 *
 * Never proof for the till that Back Office holds its provenance: only the
 * served lookup projection is evidence.
 */
exports.CardProvenanceAckStatus = zod_1.z.enum(['stored', 'absent', 'quarantined']);
/**
 * Why provenance was quarantined: the first V-rule that failed, or a replay
 * contradiction. `REFUND_MARKER_*` (Link-store design r5 §2.5) are the
 * refund-marker replay's: markers added to an unmarked refund, or a marked
 * refund dropped or changed. Back Office does not write them before step 2c.
 */
exports.CardProvenanceConflictCode = zod_1.z.enum([
    'V0',
    'V1',
    'V2',
    'V3',
    'V4',
    'V5',
    'V6',
    'V7',
    'REPLAY_AMENDMENT',
    'REPLAY_CONFLICT',
    'REFUND_MARKER_AMENDMENT',
    'REFUND_MARKER_CONFLICT',
]);
/** The card-provenance route's per-record ACK. Every other route keeps `SyncRecordAck`. */
exports.SyncCardProvenanceRecordAck = sync_js_1.SyncRecordAck.extend({
    /** Present on an accepted record only. */
    provenance: exports.CardProvenanceAckStatus.optional(),
    /** Present with `provenance: quarantined`. Never a card value. */
    provenanceConflict: exports.CardProvenanceConflictCode.optional(),
});
exports.SyncCardProvenanceUploadResponse = sync_js_1.SyncBatchAck.extend({
    acks: zod_1.z.array(exports.SyncCardProvenanceRecordAck),
});
const NonNegativeMinor = pricing_js_1.MoneyMinor.refine((value) => /^(0|[1-9]\d*)$/.test(value), {
    message: 'minor units must be a non-negative base-10 integer',
});
/**
 * One tender leg as the provenance projection serves it: an explicit
 * allow-list. Never a card number (even masked), an authorisation code, a gift
 * card number, the leg's extensions or any slip field.
 */
exports.CardProvenanceLeg = zod_1.z
    .object({
    /** Position in the sale's stored leg list. */
    legIndex: zod_1.z.number().int().min(0).max(31),
    method: zod_1.z.string().min(1).max(64),
    amountMinor: NonNegativeMinor,
    rrn: sync_js_1.CardTransactionReference.optional(),
    paymentTerminalId: sync_js_1.CardTransactionReference.optional(),
    paymentMerchantId: sync_js_1.CardTransactionReference.optional(),
    paymentBindingId: ProvenanceIdentifier.optional(),
    cardBound: zod_1.z.boolean().optional(),
})
    .strict();
/**
 * What Back Office had counted as refunded through each bound leg, from one
 * snapshot (O6). A witness, not a reservation or a cross-till budget.
 * `countedRefunds` names the refund receipts (their `clientUuid`) counted, so a
 * till can deduplicate its own refunds against it. Omitted — read as unknown —
 * when more than 64 refunds would be named or a leg's sum exceeds its amount.
 */
exports.BoundLegLedger = zod_1.z
    .object({
    snapshotAt: zod_1.z.string().datetime(),
    legs: zod_1.z.array(zod_1.z
        .object({ legIndex: zod_1.z.number().int().min(0).max(31), refundedMinor: NonNegativeMinor })
        .strict()),
    countedRefunds: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(exports.CARD_PROVENANCE_LEDGER_MAX_COUNTED),
})
    .strict();
/**
 * The fields a return lookup adds to a receipt when — and only when — its
 * provenance is complete and not quarantined. Without them a till treats every
 * line as possibly bound, exactly as before RL-847. Line markers
 * (`lines[].refundCardRrn`) are served beside these and stripped otherwise.
 */
exports.CardProvenanceLookupProjection = zod_1.z
    .object({
    cardProvenance: zod_1.z.literal(1),
    cardProvenanceLegs: zod_1.z.array(exports.CardProvenanceLeg).min(1).max(32),
    boundLegLedger: exports.BoundLegLedger.optional(),
})
    .strict();
//# sourceMappingURL=sync-card-provenance.js.map